// src/components/SEOManager.jsx
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://www.mima-italia.it';
const OG_IMAGE = `${BASE_URL}/og-mima-1200x630.jpg`;
// Pagine pubbliche da indicizzare (oltre a /blog/* e alle city pages, che
// hanno il proprio <Helmet>). Tutto il resto (login, step del form,
// dashboard, checkout, città "coming soon" servite da /:city...) riceve
// noindex: prima ogni rotta dell'app si auto-dichiarava canonical con il
// title di default, e Google la trattava come duplicato della home
// ("Pagina duplicata senza URL canonico selezionato dall'utente").
// Se aggiungi una pagina pubblica, aggiungila qui, in SEO_MAP e in
// scripts/generate-sitemap.cjs.
const INDEXABLE_PATHS = [
    '/',
    '/valori',
    '/assistenti',
    '/utenti',
    '/lavora-con-noi',
    '/faq',
    '/cookiepolicy',
    '/privacy',
    '/condizionidiuso',
];

// Le city pages (/milano, /monza, /torino, /genova, /napoli) gestiscono il
// proprio <Helmet> dentro LandingLocal.jsx (canonical, title, description
// costruiti da CITTA_CONFIG). SEOManager NON deve renderizzare nulla per
// questi path: se lo facesse, avremmo due <Helmet> in conflitto sulla
// stessa pagina — merge order non deterministico tra i due, causa reale
// delle segnalazioni "pagina alternativa con canonical" viste in Search
// Console per Torino/Genova.
const CITY_PATHS = ['/milano', '/monza', '/torino', '/genova', '/napoli'];
const OWN_HELMET_PATHS = ['/valori', '/assistenti', '/lavora-con-noi', '/faq'];

const SEO_MAP = {
    '/': {
        title: 'Assistenza anziani e badante ad ore, ABA, accompagnamento | Mima',
        description: 'Badante ad ore, accompagnamento a visite mediche, supporto ABA per disabilità, aiuto post-operatorio. Trova un assistente vicino a te, nessun abbonamento.'
    },
    '/valori': {
        title: 'I nostri valori | Mima',
        description: 'Mima nasce dalla convinzione che chiunque, in certi momenti della vita, abbia bisogno di qualcuno al proprio fianco. Scopri i valori che guidano ogni assistente Mima.'
    },
    '/assistenti': {
        title: 'Trova un\'assistente nella tua zona | Mima',
        description: 'Cerca assistenti disponibili vicino a te. Profili verificati, recensioni reali, contatto diretto. Prenoti solo le ore che ti servono, quando ti servono.'
    },
    '/utenti': {
        title: 'Come funziona Mima',
        description: 'Trova l\'assistente giusto in pochi passi: cerca nella tua zona, scegli il profilo, organizza il servizio via chat. Semplice, flessibile, senza impegni fissi.'
    },
    '/registrazione': {
        title: 'Registrati su Mima',
        description: 'Crea il tuo account Mima gratuitamente e accedi ad assistenti verificati nella tua zona. Nessun abbonamento, paghi solo il servizio che usi.'
    },
    '/cookiepolicy': {
        title: 'Cookie policy | Mima',
        description: 'Informativa sui cookie utilizzati da Mima: quali sono, a cosa servono e come gestire il tuo consenso.'
    },
    '/privacy': {
        title: 'Privacy policy | Mima',
        description: 'Informativa sul trattamento dei dati personali degli utenti e degli assistenti che usano Mima.'
    },
    '/condizionidiuso': {
        title: "Condizioni d'uso | Mima",
        description: "Le condizioni d'uso della piattaforma Mima per famiglie, utenti e assistenti."
    },
    '/login': {
        title: 'Accedi al tuo account | Mima',
        description: 'Accedi a Mima per gestire le tue prenotazioni, i messaggi e il tuo profilo.'
    }
};

const DEFAULT = {
    title: 'Assistenza on demand · Per chiunque attraversi un momento di fragilità | Mima',
    description: 'Trova un\'assistente nella tua zona, disponibile anche per poche ore. Accompagnamenti, commissioni, compagnia e supporto per te o per chi ami. Nessun abbonamento, nessun impegno fisso.',
};

export default function SEOManager() {
    const { pathname } = useLocation();

    // Normalizza: rimuove trailing slash (tranne root) e forza lowercase
    // Evita che /milano/ e /milano vengano trattati come pagine diverse da Google
    const normalizedPath = pathname === '/'
        ? '/'
        : pathname.replace(/\/+$/, '').toLowerCase();

    // Guard: le city pages gestiscono il proprio <Helmet> in LandingLocal.
    // Nessun render qui, altrimenti avremmo due <Helmet> in conflitto
    // sulla stessa pagina.
    if (CITY_PATHS.includes(normalizedPath)) {
        return null;
    }

    // Guard: l'indice blog (Blog.js) e ogni singolo articolo
    // (src/pages/blog/*.js) gestiscono già il proprio <Helmet> con
    // title/description/keywords/OG/Twitter/article tags specifici.
    // Stesso motivo delle city page: due <Helmet> sullo stesso path
    // producono un merge non deterministico e canonical in conflitto.
    if (normalizedPath === '/blog' || normalizedPath.startsWith('/blog/')) {
        return null;
    }

    // Stesso motivo: queste pagine hanno il proprio <Helmet> con canonical.
    // Senza questa guardia l'HTML prerenderizzato conteneva DUE canonical.
    if (OWN_HELMET_PATHS.includes(normalizedPath)) {
        return null;
    }

    const seo = SEO_MAP[normalizedPath] ?? DEFAULT;
    const canonical = `${BASE_URL}${normalizedPath}`;
    const noindex = !INDEXABLE_PATHS.includes(normalizedPath);

    return (
        <Helmet>
            <link rel="canonical" href={canonical} />
            <title>{seo.title}</title>
            <meta name="description" content={seo.description} />
            {noindex && <meta name="robots" content="noindex, follow" />}
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.description} />
            {/* Tag social completi anche qui: src/index.js rimuove gli og:/twitter:
                statici di index.html prima del mount, quindi se non li rimette
                Helmet il prerender salva la pagina senza immagine e le anteprime
                su WhatsApp/Facebook/LinkedIn escono senza foto. */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Mima" />
            <meta property="og:locale" content="it_IT" />
            <meta property="og:image" content={OG_IMAGE} />
            <meta property="og:image:secure_url" content={OG_IMAGE} />
            <meta property="og:image:type" content="image/jpeg" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Logo Mima" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.description} />
            <meta name="twitter:image" content={OG_IMAGE} />
        </Helmet>
    );
}